import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Archive, RotateCcw, Upload, X } from "lucide-react";
import api, { resolveImg, formatApiError } from "@/lib/api";
import { formatKWD } from "@/lib/format";

const AVAIL = [
  { v: "available", label: "متاح" },
  { v: "sold_out", label: "Sold Out (نفد)" },
  { v: "unavailable", label: "غير متاح (مخفي)" },
];
const AVAIL_LABEL = Object.fromEntries(AVAIL.map((a) => [a.v, a.label]));

const emptyForm = { name_ar: "", name_en: "", sku: "", description_ar: "", price: "", images: [], availability: "available" };

function ProductForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial ? { ...emptyForm, ...initial } : emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("images", [...f.images, data.url]);
      toast.success("تم رفع الصورة");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const moveImg = (i, dir) => {
    const c = [...f.images]; const j = i + dir;
    if (j < 0 || j >= c.length) return;
    [c[i], c[j]] = [c[j], c[i]]; set("images", c);
  };
  const makePrimary = (i) => {
    if (i === 0) return;
    const c = [...f.images]; const [img] = c.splice(i, 1); c.unshift(img); set("images", c);
  };

  const save = async () => {
    if (!f.name_ar || !f.sku || !f.price) { toast.error("الاسم والرمز والسعر مطلوبة"); return; }
    setSaving(true);
    const payload = { name_ar: f.name_ar, name_en: f.name_en || "", sku: f.sku, description_ar: f.description_ar || "",
      price: parseFloat(f.price), images: f.images, availability: f.availability, product_type: "model", variants: [] };
    try {
      if (initial?.id) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("تم الحفظ"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="product-form">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-display text-2xl">{initial?.id ? "تعديل موديل" : "موديل جديد"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div><label className="text-xs text-text-muted">الاسم بالعربية</label>
            <input placeholder="مثال: RNF 8" value={f.name_ar} onChange={(e) => set("name_ar", e.target.value)} data-testid="pf-name-ar" className="w-full border border-border px-3 py-2.5 text-sm mt-1" /></div>
          <div><label className="text-xs text-text-muted">الاسم بالإنجليزية (اختياري)</label>
            <input placeholder="Name (EN)" dir="ltr" value={f.name_en} onChange={(e) => set("name_en", e.target.value)} data-testid="pf-name-en" className="w-full border border-border px-3 py-2.5 text-sm mt-1" /></div>
          <div><label className="text-xs text-text-muted">الرمز (SKU)</label>
            <input placeholder="RNF-008" dir="ltr" value={f.sku} onChange={(e) => set("sku", e.target.value)} data-testid="pf-sku" className="w-full border border-border px-3 py-2.5 text-sm mt-1" /></div>
          <div><label className="text-xs text-text-muted">السعر (د.ك)</label>
            <input placeholder="0.000" type="number" step="0.001" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" className="w-full border border-border px-3 py-2.5 text-sm mt-1" /></div>
        </div>
        <div className="mb-4">
          <label className="text-xs text-text-muted">حالة الإتاحة</label>
          <select value={f.availability} onChange={(e) => set("availability", e.target.value)} data-testid="pf-availability" className="w-full border border-border px-3 py-2.5 text-sm mt-1">
            {AVAIL.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
          </select>
          <p className="text-[11px] text-text-muted mt-1">لا يوجد مخزون — التحكم يدوي بحالة الموديل فقط.</p>
        </div>
        <textarea placeholder="الوصف بالعربية" rows={2} value={f.description_ar} onChange={(e) => set("description_ar", e.target.value)} data-testid="pf-desc-ar" className="w-full border border-border px-3 py-2.5 text-sm mb-4" />

        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-text-muted">الصور (الأولى هي الرئيسية)</span>
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pf-upload-btn" className="flex items-center gap-1.5 text-xs border border-border px-3 py-1.5 hover:border-primary">
              <Upload className="w-3.5 h-3.5" /> {uploading ? "جارٍ الرفع…" : "رفع صورة"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {f.images.map((img, i) => (
              <div key={i} className="relative w-20 h-24 bg-surface">
                {i === 0 && <span className="absolute top-0 start-0 bg-[#6B563F] text-white text-[9px] px-1 z-10">رئيسية</span>}
                <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => set("images", f.images.filter((_, idx) => idx !== i))} data-testid={`pf-img-remove-${i}`} className="absolute top-1 end-1 bg-white/90 p-0.5"><X className="w-3 h-3" /></button>
                <div className="absolute bottom-0 inset-x-0 flex justify-between items-center text-[10px] bg-white/85">
                  <button onClick={() => moveImg(i, -1)} className="px-1">‹</button>
                  {i !== 0 && <button onClick={() => makePrimary(i)} data-testid={`pf-img-primary-${i}`} className="px-1 text-[#6B563F]">تعيين رئيسية</button>}
                  <button onClick={() => moveImg(i, 1)} className="px-1">›</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="pf-save-btn" className="w-full bg-[#141110] text-white py-3.5 text-sm font-medium hover:bg-black transition-colors disabled:opacity-50">
          {saving ? "…" : "حفظ الموديل"}
        </button>
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const archive = async (p) => {
    const msg = p.status === "active" ? `أرشفة الموديل ${p.name_ar}؟ لن يظهر في المتجر.` : `استعادة الموديل ${p.name_ar}؟`;
    if (!window.confirm(msg)) return;
    try { await api.post(`/admin/products/${p.id}/${p.status === "active" ? "archive" : "restore"}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onSaved = () => { setEditing(null); setCreating(false); load(); };

  return (
    <div className="p-5 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl">الموديلات</h1>
        <button onClick={() => setCreating(true)} data-testid="add-product-btn" className="flex items-center gap-2 bg-[#141110] text-white px-4 py-2.5 text-sm hover:bg-black transition-colors">
          <Plus className="w-4 h-4" /> إضافة موديل جديد
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className={`bg-white border border-border ${p.status === "archived" ? "opacity-60" : ""}`} data-testid={`admin-product-${p.sku}`}>
            <div className="flex gap-3 p-3">
              <div className="w-20 h-24 bg-surface overflow-hidden flex-shrink-0">
                {p.images?.[0] && <img src={resolveImg(p.images[0])} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm line-clamp-1">{p.name_ar}</p>
                <p className="text-xs text-text-muted mt-0.5">{p.sku}</p>
                <p className="text-xs mt-1">{formatKWD(p.price)}</p>
                <p className="text-xs mt-1"><span className={`px-1.5 py-0.5 ${p.availability === "available" ? "bg-green-100 text-green-800" : p.availability === "sold_out" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-600"}`} data-testid={`avail-${p.sku}`}>{AVAIL_LABEL[p.availability] || "متاح"}</span></p>
                {p.status === "archived" && <span className="text-[10px] text-destructive">مؤرشف</span>}
              </div>
            </div>
            <div className="flex border-t border-border text-xs">
              <button onClick={() => setEditing(p)} data-testid={`edit-product-${p.sku}`} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 hover:bg-surface border-e border-border">
                <Pencil className="w-3.5 h-3.5" /> تعديل
              </button>
              <button onClick={() => archive(p)} data-testid={`archive-product-${p.sku}`} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 hover:bg-surface">
                {p.status === "active" ? <><Archive className="w-3.5 h-3.5" /> أرشفة</> : <><RotateCcw className="w-3.5 h-3.5" /> استعادة</>}
              </button>
            </div>
          </div>
        ))}
      </div>

      {(creating || editing) && (
        <ProductForm initial={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={onSaved} />
      )}
    </div>
  );
}
